import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar, Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-black overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
      
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF6600] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>

      <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-full px-5 py-2 mb-6">
              <Calendar className="w-4 h-4 text-[#FF6600]" />
              <span className="text-[#FF6600] font-semibold text-sm">Get Started Today</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Ready to <span className="text-gradient-orange">Transform</span> Your Sales?
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Book a call with our team to discover how AI can drive 70% growth in your organization.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Contact Form */}
            <Card className="glass-morphism border-white/10">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Book a Call</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF6600] focus:ring-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Work Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF6600] focus:ring-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company Name *
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Your Company Inc."
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF6600] focus:ring-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="888-256-2216"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF6600] focus:ring-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tell us about your needs
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="I'm interested in learning more about..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF6600] focus:ring-[#FF6600] resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FF6600] hover:bg-[#FF8C42] text-white font-semibold py-6 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-[#FF6600]/50"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Schedule a Call
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Right - Contact Info & Details */}
            <div className="space-y-8">
              <Card className="glass-morphism border-white/10">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#FF6600]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Email</h4>
                        <p className="text-gray-400">contact@intellapoint.com</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#FF6600]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Phone</h4>
                        <p className="text-gray-400">888-256-2216</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#FF6600]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Office</h4>
                        <p className="text-gray-400">Orange County, CA</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-morphism border-white/10 bg-gradient-to-br from-[#FF6600]/10 to-transparent">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-white mb-4">What to Expect</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-[#FF6600] mr-2 mt-1">✓</span>
                      <span>30-minute discovery call to understand your needs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#FF6600] mr-2 mt-1">✓</span>
                      <span>Custom AI strategy tailored to your business</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#FF6600] mr-2 mt-1">✓</span>
                      <span>ROI analysis and implementation roadmap</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#FF6600] mr-2 mt-1">✓</span>
                      <span>Live demo of OpenClaw Agents in action</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
